import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Users, Briefcase, Award, Shield } from "lucide-react";
import { Link } from "react-router-dom";
import homevideo from"../Assets/Homevideo.mp4";

export default function Home() {

  const stats = [
    { value: "500+", label: "Clients Served" },
    { value: "10K+", label: "Placements" },
    { value: "15+", label: "Years Experience" },
    { value: "98%", label: "Client Satisfaction" }
  ];

  const features = [
    {
      icon: Users,
      title: "Strategic Talent Acquisition",
      desc: "Identify and recruit professionals who align with your business strategy.",
      color: "text-blue-600"
    },
    {
      icon: Briefcase,
      title: "HR Transformation",
      desc: "Modern HR systems designed to increase efficiency and employee engagement.",
      color: "text-purple-600"
    },
    {
      icon: Award,
      title: "Industry Expertise",
      desc: "Extensive experience delivering HR consulting across multiple sectors.",
      color: "text-amber-500"
    },
    {
      icon: Shield,
      title: "Compliance & Governance",
      desc: "Ensure complete compliance with employment regulations and HR policies.",
      color: "text-green-600"
    }
  ];


  const services = [
    "Talent Acquisition",
    "Recruitment Process Outsourcing",
    "HR Consulting",
    "Payroll Management",
    "Employee Training",
    "HR Compliance"
  ];


  return (

    <main className="bg-white text-black">

      {/* HERO WITH VIDEO */}

      <section className="relative pt-32 pb-24 px-6 overflow-hidden">

        {/* Background Video */}

        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20"
        >
          <source src={homevideo} type="video/mp4" />
        </video>


        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

          {/* TEXT */}

          <motion.div
            initial={{opacity:0,x:-40}}
            animate={{opacity:1,x:0}}
            transition={{duration:0.7}}
          >

            <span className="text-sm font-medium text-gray-500">
              Strategic HR Consultancy
            </span>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-4 mb-6 leading-tight">
              Build High-Performance Teams
              <span className="block text-gray-600">
                That Drive Business Success
              </span>
            </h1>

            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              We partner with organizations to transform workforce strategies,
              attract exceptional talent, and develop leadership capabilities
              that support long-term growth.
            </p>

            <div className="flex gap-4 flex-wrap">

              <Link to="/contact">

                <motion.button
                  whileHover={{scale:1.05}}
                  whileTap={{scale:0.97}}
                  className="bg-black text-white px-8 py-3 font-semibold flex items-center gap-2"
                >
                  Get Consultation
                  <ArrowRight size={18}/>
                </motion.button>

              </Link>


              <Link to="/services">

                <motion.button
                  whileHover={{scale:1.05}}
                  whileTap={{scale:0.97}}
                  className="border border-black px-8 py-3 font-semibold hover:bg-black hover:text-white transition"
                >
                  Our Services
                </motion.button>

              </Link>

            </div>

          </motion.div>



          {/* STATS CARD */}

          <motion.div
            initial={{opacity:0,x:40}}
            animate={{opacity:1,x:0}}
            transition={{duration:0.7}}
          >

            <div className="bg-white/80 backdrop-blur-md rounded-xl p-10 shadow-lg">

              <div className="grid grid-cols-2 gap-6">

                {stats.map((stat,i)=>(

                  <div key={i} className="text-center">

                    <h3 className="text-3xl font-bold">
                      {stat.value}
                    </h3>

                    <p className="text-gray-500 text-sm">
                      {stat.label}
                    </p>

                  </div>

                ))}

              </div>

            </div>

          </motion.div>

        </div>

      </section>



      {/* WHY TRUST US */}

      <section className="py-24 px-6 border-t border-gray-200">

        <div className="max-w-7xl mx-auto">

          <div className="text-center mb-16">

            <h2 className="text-4xl font-bold mb-4">
              Why Organizations Trust Us
            </h2>

            <p className="text-gray-600 max-w-xl mx-auto">
              Our expertise helps organizations strengthen leadership,
              attract top talent, and build resilient teams.
            </p>

          </div>


          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">

            {features.map((item,i)=>{

              const Icon = item.icon;

              return(

                <motion.div
                  key={i}
                  initial={{opacity:0,y:30}}
                  whileInView={{opacity:1,y:0}}
                  transition={{duration:0.6,delay:i*0.1}}
                  viewport={{once:true}}
                  whileHover={{y:-6}}

                  className="p-6 bg-white border border-gray-200 shadow-sm hover:shadow-lg transition"
                >

                  <Icon
                    size={36}
                    className={`${item.color} mb-4`}
                  />

                  <h3 className="font-semibold text-lg mb-2">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 text-sm leading-relaxed">
                    {item.desc}
                  </p>

                </motion.div>

              )

            })}

          </div>

        </div>

      </section>



      {/* SERVICES */}

      <section className="py-24 px-6 bg-gray-50">

        <div className="max-w-7xl mx-auto text-center">

          <h2 className="text-4xl font-bold mb-6">
            Our Core Services
          </h2>

          <p className="text-gray-600 mb-12 max-w-xl mx-auto">
            Comprehensive HR solutions designed to help your organization
            attract, manage and develop top talent.
          </p>


          <div className="grid md:grid-cols-3 gap-8">

            {services.map((service,i)=>(

              <motion.div
                key={i}
                whileHover={{y:-6}}
                className="p-8 bg-white border border-gray-200 shadow-sm hover:shadow-lg transition"
              >

                <h3 className="font-semibold text-lg">
                  {service}
                </h3>

              </motion.div>

            ))}

          </div>


          <div className="mt-12">

            <Link to="/services">

              <motion.button
                whileHover={{scale:1.05}}
                whileTap={{scale:0.97}}
                className="bg-black text-white px-8 py-3 font-semibold"
              >
                View All Services
              </motion.button>

            </Link>

          </div>

        </div>

      </section>



      {/* CTA */}

      <section className="bg-black text-white py-20 px-6">

        <div className="max-w-5xl mx-auto text-center">

          <h2 className="text-4xl font-bold mb-6">
            Ready to Strengthen Your Workforce?
          </h2>

          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Speak with our HR experts and discover how we can help
            your organization build stronger teams.
          </p>


          <Link to="/contact">

            <motion.button
              whileHover={{scale:1.05}}
              whileTap={{scale:0.97}}
              className="bg-white text-black px-8 py-3 font-semibold"
            >
              Contact Us
            </motion.button>

          </Link>

        </div>

      </section>

    </main>
  );
}